/**
 * Predictive observability surfaces for the pool admin (IL-137).
 *
 * Three views, all read-only and refreshed by the parent page on its
 * normal cadence:
 *
 *   1. **Forecast vs. actual** — large line chart with a real Y axis in
 *      session slots, time X axis rendered in the pool's timezone, gridlines,
 *      a legend + direct end-labels, and per-record hover tooltips. Forecast
 *      is the accent line; the capped "contributed" signal is the filled step
 *      area under it; actual demand is the dashed gray line (dash = secondary
 *      encoding so identity never rides on color alone).
 *
 *   2. **Bin-fill heatmap** — the 7×96 weekly grid (or 1×96 diurnal),
 *      colored by per-bin nEff with an explicit intensity legend in
 *      effective-sample units. Hour axis + weekday rows labeled.
 *
 *   3. **Quarantine list** — recent AnomalyGate rejections with human bin
 *      labels (e.g. "Tue 14:30") and observations in busy session slots.
 *
 * No external chart library — inline SVG for the chart, CSS Grid for the
 * heatmap. All colors come from design tokens so light/dark themes both work.
 */

import React, { useMemo } from "react";
import styles from "../routes/admin/pools/PoolDetail.module.css";

interface PredictionRow {
  timestampMs: number;
  forecastValue: number;
  contributed: number;
  actualDemand: number;
  coldStartStage: number;
  nEff: number;
  mode: string;
  region: string | null;
  reason: string | null;
}

interface BinRow {
  tBin: number;
  nEff: number;
  weeksObserved: number;
  lastUpdatedAt: number;
}

interface QuarantineRow {
  id: number;
  tBin: number;
  observation: number;
  reason: string;
  atMs: number;
}

interface Props {
  predictions: PredictionRow[];
  bins: BinRow[];
  quarantine: QuarantineRow[];
  tz: string;
}

const DAYS_OF_WEEK = ["Mon", "Tue", "Wed", "Thu", "Fri", "Sat", "Sun"];
const QH_PER_DAY = 96;

export function PredictiveObservability({ predictions, bins, quarantine, tz }: Props) {
  const sortedPredictions = useMemo(
    () => [...predictions].sort((a, b) => a.timestampMs - b.timestampMs),
    [predictions],
  );

  // Auto-detect histogram mode by bin count: 672 = weekly, 96 = diurnal.
  const binMode: "weekly" | "diurnal" = bins.length <= QH_PER_DAY ? "diurnal" : "weekly";

  return (
    <>
      <ForecastVsActualChart predictions={sortedPredictions} tz={tz} />
      <BinFillHeatmap bins={bins} tz={tz} mode={binMode} />
      <QuarantineList rows={quarantine} mode={binMode} />
    </>
  );
}

// ---------------------------------------------------------------------------
// Time / number formatting
// ---------------------------------------------------------------------------

function makeTimeFormatter(tz: string, withDay: boolean): (ms: number) => string {
  try {
    const fmt = new Intl.DateTimeFormat("en-GB", {
      timeZone: tz || "UTC",
      ...(withDay ? { weekday: "short" } : {}),
      hour: "2-digit",
      minute: "2-digit",
      hour12: false,
    });
    return (ms: number) => fmt.format(new Date(ms));
  } catch {
    // Invalid IANA name from config — fall back to UTC rather than crash.
    const fmt = new Intl.DateTimeFormat("en-GB", {
      timeZone: "UTC",
      ...(withDay ? { weekday: "short" } : {}),
      hour: "2-digit",
      minute: "2-digit",
      hour12: false,
    });
    return (ms: number) => fmt.format(new Date(ms));
  }
}

/** Round up to a "nice" 1/2/5×10^k step ≥ raw. */
function niceStep(raw: number): number {
  if (raw <= 0) return 1;
  const mag = Math.pow(10, Math.floor(Math.log10(raw)));
  for (const m of [1, 2, 5, 10]) {
    if (m * mag >= raw) return m * mag;
  }
  return 10 * mag;
}

function formatQh(qh: number): string {
  const h = Math.floor(qh / 4);
  const m = (qh % 4) * 15;
  return `${String(h).padStart(2, "0")}:${String(m).padStart(2, "0")}`;
}

function formatBinLabel(tBin: number, mode: "weekly" | "diurnal"): string {
  if (mode === "diurnal") return formatQh(tBin);
  const dow = Math.floor(tBin / QH_PER_DAY);
  return `${DAYS_OF_WEEK[dow] ?? `d${dow}`} ${formatQh(tBin % QH_PER_DAY)}`;
}

// ---------------------------------------------------------------------------
// Forecast vs. actual — large SVG line chart with axes in session slots
// ---------------------------------------------------------------------------

// ViewBox geometry. Rendered at width:100%, so these are proportions more
// than pixels; the ~2.5:1 aspect keeps the chart tall enough to read slopes.
const W = 880;
const H = 330;
const MARGIN = { top: 18, right: 104, bottom: 40, left: 56 };
const PLOT_W = W - MARGIN.left - MARGIN.right;
const PLOT_H = H - MARGIN.top - MARGIN.bottom;

function ForecastVsActualChart({ predictions, tz }: { predictions: PredictionRow[]; tz: string }) {
  if (predictions.length === 0) {
    return (
      <div className={styles.chartCard}>
        <div className={styles.chartHeader}>
          <h5 className={styles.chartTitle}>Forecast vs. Actual Demand</h5>
        </div>
        <div className={styles.chartEmpty}>
          No predictions logged yet. Histograms in stage 0 (cold-start) emit floor=0
          while the model accumulates ≥ 4 weeks of per-bin data — seed from session
          history above to skip the wait.
        </div>
      </div>
    );
  }

  const minTs = predictions[0]!.timestampMs;
  const maxTs = predictions[predictions.length - 1]!.timestampMs;
  const span = Math.max(1, maxTs - minTs);

  let rawMax = 1;
  for (const p of predictions) {
    if (p.forecastValue > rawMax) rawMax = p.forecastValue;
    if (p.actualDemand > rawMax) rawMax = p.actualDemand;
    if (p.contributed > rawMax) rawMax = p.contributed;
  }
  const yStep = niceStep((rawMax * 1.1) / 4);
  const yMax = yStep * Math.max(1, Math.ceil((rawMax * 1.1) / yStep));
  const yTicks: number[] = [];
  for (let v = 0; v <= yMax + 1e-9; v += yStep) yTicks.push(v);

  const xOf = (ts: number) => MARGIN.left + ((ts - minTs) / span) * PLOT_W;
  const yOf = (v: number) => MARGIN.top + PLOT_H - (Math.min(v, yMax) / yMax) * PLOT_H;

  const linePath = (get: (p: PredictionRow) => number) => predictions
    .map((p, i) => `${i === 0 ? "M" : "L"} ${xOf(p.timestampMs).toFixed(1)} ${yOf(get(p)).toFixed(1)}`)
    .join(" ");

  const forecastPath = linePath(p => p.forecastValue);
  const actualPath = linePath(p => p.actualDemand);
  // Contributed = what predictive actually handed to mediation (0 in shadow /
  // cold-start, capped by clamps). Filled step-area anchored to the baseline.
  const contributedArea = `${linePath(p => p.contributed)} L ${xOf(maxTs).toFixed(1)} ${yOf(0).toFixed(1)} L ${xOf(minTs).toFixed(1)} ${yOf(0).toFixed(1)} Z`;

  // X ticks: 5 evenly spaced; include the weekday once the span crosses a day.
  const fmtTime = makeTimeFormatter(tz, span > 36 * 3_600_000);
  const fmtTooltip = makeTimeFormatter(tz, true);
  const X_TICK_COUNT = 5;
  const xTicks = Array.from({ length: X_TICK_COUNT }, (_, i) => minTs + (span * i) / (X_TICK_COUNT - 1));

  // Summary strip
  const totalForecasts = predictions.length;
  const shadowCount = predictions.filter((p) => p.mode === "shadow").length;
  const stage2Count = predictions.filter((p) => p.coldStartStage === 2).length;
  const capCount = predictions.filter((p) => p.contributed < p.forecastValue && p.mode === "predictive").length;
  // Coverage = fraction of records where actual ≤ forecast (P_x sized correctly)
  const coverageRecords = predictions.filter((p) => p.coldStartStage > 0);
  const covered = coverageRecords.filter((p) => p.actualDemand <= p.forecastValue).length;
  const coveragePct = coverageRecords.length > 0
    ? (100 * covered) / coverageRecords.length
    : null;

  // Direct end-labels with minimal collision avoidance.
  const last = predictions[predictions.length - 1]!;
  let forecastLabelY = yOf(last.forecastValue);
  let actualLabelY = yOf(last.actualDemand);
  if (Math.abs(forecastLabelY - actualLabelY) < 14) {
    if (forecastLabelY <= actualLabelY) {
      forecastLabelY -= (14 - Math.abs(forecastLabelY - actualLabelY)) / 2;
      actualLabelY = forecastLabelY + 14;
    } else {
      actualLabelY -= (14 - Math.abs(forecastLabelY - actualLabelY)) / 2;
      forecastLabelY = actualLabelY + 14;
    }
  }

  const hitWidth = Math.max(2, PLOT_W / predictions.length);

  return (
    <div className={styles.chartCard}>
      <div className={styles.chartHeader}>
        <h5 className={styles.chartTitle}>Forecast vs. Actual Demand</h5>
        <span className={styles.chartMeta}>
          {totalForecasts} records · {stage2Count} mature · {shadowCount} shadow
          {capCount > 0 ? ` · ${capCount} capped` : ""}
          {coveragePct !== null ? ` · coverage ${coveragePct.toFixed(0)}%` : ""}
          {" · times in "}{tz || "UTC"}
        </span>
      </div>
      <svg
        viewBox={`0 0 ${W} ${H}`}
        className={styles.chartSvg}
        role="img"
        aria-label={`Forecast versus actual demand in session slots over time, ${totalForecasts} records`}
      >
        {/* Horizontal gridlines + Y tick labels (session slots) */}
        {yTicks.map(v => (
          <g key={`y-${v}`}>
            <line
              x1={MARGIN.left}
              x2={MARGIN.left + PLOT_W}
              y1={yOf(v)}
              y2={yOf(v)}
              stroke="var(--border)"
              strokeWidth={v === 0 ? 1.5 : 1}
            />
            <text
              x={MARGIN.left - 8}
              y={yOf(v) + 3.5}
              textAnchor="end"
              fontSize={11}
              fill="var(--text-secondary)"
              fontFamily="var(--font-mono, monospace)"
            >
              {v}
            </text>
          </g>
        ))}

        {/* Y axis unit label */}
        <text
          x={14}
          y={MARGIN.top + PLOT_H / 2}
          transform={`rotate(-90, 14, ${MARGIN.top + PLOT_H / 2})`}
          textAnchor="middle"
          fontSize={10}
          letterSpacing={1.5}
          fill="var(--text-secondary)"
          style={{ textTransform: "uppercase" }}
        >
          Session slots
        </text>

        {/* X ticks (time, pool TZ) */}
        {xTicks.map((ts, i) => (
          <g key={`x-${i}`}>
            <line
              x1={xOf(ts)}
              x2={xOf(ts)}
              y1={MARGIN.top + PLOT_H}
              y2={MARGIN.top + PLOT_H + 4}
              stroke="var(--text-tertiary)"
              strokeWidth={1}
            />
            <text
              x={xOf(ts)}
              y={MARGIN.top + PLOT_H + 18}
              textAnchor={i === 0 ? "start" : i === xTicks.length - 1 ? "end" : "middle"}
              fontSize={11}
              fill="var(--text-secondary)"
              fontFamily="var(--font-mono, monospace)"
            >
              {fmtTime(ts)}
            </text>
          </g>
        ))}

        {/* Contributed step-area (what mediation actually received) */}
        <path
          d={contributedArea}
          fill="var(--color-accent)"
          opacity={0.12}
          stroke="none"
        />

        {/* Actual demand — dashed gray (dash = secondary encoding) */}
        <path d={actualPath} stroke="var(--gray-6)" strokeWidth={2} strokeDasharray="5 3" fill="none" />

        {/* Forecast — solid accent */}
        <path d={forecastPath} stroke="var(--color-accent)" strokeWidth={2} fill="none" />

        {/* Direct end-labels */}
        <text
          x={MARGIN.left + PLOT_W + 6}
          y={forecastLabelY + 3.5}
          fontSize={11}
          fill="var(--text-primary)"
          fontWeight={600}
        >
          Forecast
        </text>
        <text
          x={MARGIN.left + PLOT_W + 6}
          y={actualLabelY + 3.5}
          fontSize={11}
          fill="var(--text-secondary)"
        >
          Actual
        </text>

        {/* Hover layer: one hit column per record with a native tooltip */}
        {predictions.map((p, i) => (
          <rect
            key={`hit-${i}`}
            x={xOf(p.timestampMs) - hitWidth / 2}
            y={MARGIN.top}
            width={hitWidth}
            height={PLOT_H}
            fill="transparent"
          >
            <title>
              {`${fmtTooltip(p.timestampMs)} (${tz || "UTC"})\nforecast ${p.forecastValue.toFixed(1)} slots · contributed ${p.contributed.toFixed(1)} · actual ${p.actualDemand.toFixed(1)}\nmode ${p.mode} · cold-start stage ${p.coldStartStage} · nEff ${p.nEff.toFixed(1)}${p.reason ? `\n${p.reason}` : ""}`}
            </title>
          </rect>
        ))}
      </svg>
      <div className={styles.chartLegend}>
        <span className={styles.chartLegendItem} aria-hidden="true">
          <svg width="22" height="8"><line x1="0" y1="4" x2="22" y2="4" stroke="var(--color-accent)" strokeWidth="2" /></svg>
          forecast (session slots)
        </span>
        <span className={styles.chartLegendItem} aria-hidden="true">
          <svg width="22" height="8"><line x1="0" y1="4" x2="22" y2="4" stroke="var(--gray-6)" strokeWidth="2" strokeDasharray="5 3" /></svg>
          actual demand (session slots)
        </span>
        <span className={styles.chartLegendItem} aria-hidden="true">
          <svg width="22" height="10"><rect x="0" y="1" width="22" height="8" fill="var(--color-accent)" opacity="0.12" /></svg>
          contributed to mediation
        </span>
      </div>
    </div>
  );
}

// ---------------------------------------------------------------------------
// Bin-fill heatmap — CSS Grid, sequential accent ramp with an nEff legend
// ---------------------------------------------------------------------------

function heatColor(intensity: number): string {
  // Sequential single-hue ramp from the accent token so both themes work.
  // 12%..82% opacity of the accent over the surface.
  const pct = Math.round(12 + intensity * 70);
  return `color-mix(in srgb, var(--color-accent) ${pct}%, transparent)`;
}

function BinFillHeatmap({ bins, tz, mode }: { bins: BinRow[]; tz: string; mode: "weekly" | "diurnal" }) {
  const rows = mode === "weekly" ? 7 : 1;
  const cols = QH_PER_DAY;

  const byKey = useMemo(() => {
    const m = new Map<number, BinRow>();
    for (const b of bins) m.set(b.tBin, b);
    return m;
  }, [bins]);

  let maxNEff = 0;
  for (const b of bins) if (b.nEff > maxNEff) maxNEff = b.nEff;

  const filled = bins.length;
  const total = mode === "weekly" ? 7 * QH_PER_DAY : QH_PER_DAY;

  // Legend swatches: empty + 4 intensity steps labeled in nEff units.
  const legendSteps = [0.25, 0.5, 0.75, 1];

  return (
    <div className={styles.chartCard}>
      <div className={styles.chartHeader}>
        <h5 className={styles.chartTitle}>
          Histogram Bin Fill ({mode === "weekly" ? "weekly, 7×96 quarter-hours" : "diurnal, 96 quarter-hours"})
        </h5>
        <span className={styles.chartMeta}>
          {filled}/{total} bins filled ({((100 * filled) / total).toFixed(0)}%)
          {" · bin boundaries in "}{tz || "UTC"}
        </span>
      </div>
      <div style={{ display: "grid", gridTemplateColumns: `44px repeat(${cols}, 1fr)`, gap: 1, maxWidth: "100%" }}>
        {/* Hour-of-day axis labels (every 2 hours) */}
        <div style={{ fontSize: "var(--text-xs)", color: "var(--text-tertiary)", display: "flex", alignItems: "flex-end" }}>h→</div>
        {Array.from({ length: cols }).map((_, qh) => (
          <div key={`hdr-${qh}`} style={{ fontSize: "var(--text-xs)", color: "var(--text-secondary)", textAlign: "left", height: 14, fontVariantNumeric: "tabular-nums" }}>
            {qh % 8 === 0 ? `${String(qh / 4).padStart(2, "0")}` : ""}
          </div>
        ))}
        {/* Rows */}
        {Array.from({ length: rows }).map((_, dow) => (
          <React.Fragment key={`row-${dow}`}>
            <div style={{ fontSize: "var(--text-xs)", color: "var(--text-secondary)", paddingRight: "var(--space-1)", height: 18, display: "flex", alignItems: "center" }}>
              {mode === "weekly" ? DAYS_OF_WEEK[dow] : "day"}
            </div>
            {Array.from({ length: cols }).map((_, qh) => {
              const tBin = mode === "weekly" ? dow * QH_PER_DAY + qh : qh;
              const bin = byKey.get(tBin);
              const intensity = bin && maxNEff > 0 ? Math.min(1, bin.nEff / maxNEff) : 0;
              const label = mode === "weekly" ? `${DAYS_OF_WEEK[dow]} ${formatQh(qh)}` : formatQh(qh);
              const tooltip = bin
                ? `${label} — nEff ${bin.nEff.toFixed(1)} effective samples · ${bin.weeksObserved} week(s) observed`
                : `${label} — no data yet`;
              return (
                <div
                  key={tBin}
                  title={tooltip}
                  style={{
                    backgroundColor: bin ? heatColor(intensity) : "transparent",
                    border: "0.5px solid var(--border)",
                    height: 18,
                  }}
                />
              );
            })}
          </React.Fragment>
        ))}
      </div>
      <div className={styles.chartLegend}>
        <span className={styles.chartLegendItem}>
          <span style={{ width: 14, height: 14, border: "0.5px solid var(--border)", display: "inline-block" }} />
          0 (empty)
        </span>
        {legendSteps.map(step => (
          <span key={step} className={styles.chartLegendItem}>
            <span style={{ width: 14, height: 14, border: "0.5px solid var(--border)", display: "inline-block", backgroundColor: heatColor(step) }} />
            {maxNEff > 0 ? `${(maxNEff * step).toFixed(1)}` : "—"}
          </span>
        ))}
        <span style={{ color: "var(--text-tertiary)" }}>effective samples per bin (nEff)</span>
      </div>
    </div>
  );
}

// ---------------------------------------------------------------------------
// Quarantine list — AnomalyGate rejections with units
// ---------------------------------------------------------------------------

function QuarantineList({ rows, mode }: { rows: QuarantineRow[]; mode: "weekly" | "diurnal" }) {
  return (
    <div className={styles.chartCard}>
      <div className={styles.chartHeader}>
        <h5 className={styles.chartTitle}>Quarantined Observations</h5>
        <span className={styles.chartMeta}>
          {rows.length === 0 ? "no rejections" : `${rows.length} recent rejection(s)`}
        </span>
      </div>
      {rows.length === 0 ? (
        <div className={styles.chartEmpty}>
          The AnomalyGate hasn't rejected any observations in the recent window.
          Rejections appear here when an observation is an outlier (&gt; 4·IQR from
          P50), pool is unhealthy, dampen is active, or it's a holiday.
        </div>
      ) : (
        <div style={{ maxHeight: 240, overflowY: "auto", border: "var(--border-width) solid var(--border)" }}>
          <table style={{ width: "100%", fontSize: "var(--text-xs)", borderCollapse: "collapse" }}>
            <thead style={{ position: "sticky", top: 0, background: "var(--bg-primary)" }}>
              <tr style={{ textAlign: "left" }}>
                <th style={cellStyle}>When</th>
                <th style={cellStyle}>Bin (local)</th>
                <th style={cellStyle}>Observation (busy slots)</th>
                <th style={cellStyle}>Reason</th>
              </tr>
            </thead>
            <tbody>
              {rows.map((r) => (
                <tr key={r.id} style={{ borderTop: "var(--border-width) solid var(--border)" }}>
                  <td style={cellStyle}>{new Date(r.atMs).toLocaleString()}</td>
                  <td style={{ ...cellStyle, fontFamily: "var(--font-mono, monospace)" }}>{formatBinLabel(r.tBin, mode)}</td>
                  <td style={{ ...cellStyle, fontVariantNumeric: "tabular-nums" }}>{r.observation.toFixed(1)}</td>
                  <td style={cellStyle}>{r.reason}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
    </div>
  );
}

const cellStyle: React.CSSProperties = {
  padding: "var(--space-1) var(--space-2)",
  textAlign: "left",
  fontWeight: "normal",
};
